import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import {
  ORBI_APPOINTMENT_MAP_PREVIEW,
  ORBI_MAX_VISIT_HISTORY_CARDS,
  ORBI_VISIT_HISTORY_RATE_LABEL,
  ORBI_VISIT_HISTORY_SEE_ALL_LABEL,
} from '@patient-ui/shared/constants';
import { OrbiAppointment } from '@patient-ui/shared/models';
import {
  formatOrbiAppointmentDateTime,
  orbiHasMapPreview,
  orbiHasMappableDestination,
  orbiMapPreviewUrl,
  orbiMapUrl,
  orbiRateVisitUrl,
  orbiVisitHistoryTitle,
} from '@patient-ui/shared/utils';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/**
 * Renders the Visit History card in the Orbi chat thread — the `VisitHistory`
 * return type's card.
 *
 * Structure mirrors mobile's `OrbiVisitHistoryCard` → `CardCarousel`, which is
 * what the approved Figma frames draw: ONE outer card owning the
 * `history` + "Visit History" heading, with each visit's details in its OWN
 * bordered inner card. That nesting is the shape of this card and is what
 * distinguishes it from the Upcoming Appointment card, where the heading belongs
 * to each card and multiples are a flat stack.
 *
 * Web differences from mobile, both deliberate:
 *  - mobile puts multiples in a horizontal `SwipeableCarousel`; the panel STACKS
 *    them vertically, as the Upcoming Appointment and Results cards already do,
 *    so every visit stays keyboard-reachable without a scroll container. The
 *    Figma frames are drawn as a vertical stack too.
 *  - "See All" renders only for MULTIPLE visits (mobile gates it inside its
 *    carousel branch, and the single-visit Figma frame has no such button) — one
 *    visit is already the whole history worth showing here.
 *
 * The per-visit body (date → reason → map → address → Rate Visit) is mobile's
 * `_VisitBody` in the same order with the same gaps. Presentational only: both
 * "Rate Visit" and "See All" bubble up, since the host thread owns navigation
 * and closing the panel.
 */
@Component({
  selector: 'patient-ui-orbi-visit-history-card',
  templateUrl: './orbi-visit-history-card.component.html',
  styleUrls: ['./orbi-visit-history-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiVisitHistoryCardComponent {
  /** The mapped past visits to render (already normalized by the mapper). */
  @Input() visits: OrbiAppointment[] = [];

  /** Emitted when the user asks to see the full visit history. */
  @Output() seeAll = new EventEmitter<void>();

  /** Emitted when the user rates one visit. */
  @Output() rateVisit = new EventEmitter<OrbiAppointment>();

  readonly rateLabel = ORBI_VISIT_HISTORY_RATE_LABEL;
  readonly seeAllLabel = ORBI_VISIT_HISTORY_SEE_ALL_LABEL;

  /** Map preview geometry, mirrored into the `<img>` so it reserves its box. */
  readonly mapPreview = ORBI_APPOINTMENT_MAP_PREVIEW;

  constructor(private readonly environmentService: EnvironmentService) {}

  /** The visits actually rendered — capped at mobile's 3. */
  get visibleVisits(): OrbiAppointment[] {
    return this.visits.slice(0, ORBI_MAX_VISIT_HISTORY_CARDS);
  }

  /** True when exactly one visit is present (the single-case layout). */
  get isSingle(): boolean {
    return this.visits.length === 1;
  }

  /** The card heading — "Visit History", at any count (PCSA-13516). */
  get title(): string {
    return orbiVisitHistoryTitle(this.visibleVisits.length);
  }

  /**
   * True when "See All" renders: multiple visits only, matching mobile's
   * carousel-only gate and the Figma frames.
   */
  get hasSeeAll(): boolean {
    return this.visibleVisits.length > 1;
  }

  /** The formatted date/time line, or '' when the payload carried neither. */
  dateTimeLine(visit: OrbiAppointment): string {
    return formatOrbiAppointmentDateTime(visit);
  }

  /**
   * The Rate Your Lab Visit URL for one visit, or '' when it cannot be built —
   * the control is then omitted rather than offering a dead link (see
   * `orbiRateVisitUrl` for the three guards).
   */
  rateUrl(visit: OrbiAppointment): string {
    return orbiRateVisitUrl(visit, this.environmentService.rateVisitBaseUrl);
  }

  /** True when this visit can be rated. */
  canRate(visit: OrbiAppointment): boolean {
    return this.rateUrl(visit).length > 0;
  }

  // Map link + preview tile are shared with the Upcoming Appointment card (see
  // `orbi-map.util`) — both render the same `OrbiAppointment` row, and mobile
  // shares one `MapPreviewTile` between them. These four stay as thin methods so
  // the template can keep calling them per row.

  /** Google Maps link for the visit's location, or '' when unmappable. */
  mapUrl(visit: OrbiAppointment): string {
    return orbiMapUrl(visit);
  }

  /** True when the visit has somewhere the user can actually be sent. */
  hasMappableDestination(visit: OrbiAppointment): boolean {
    return orbiHasMappableDestination(visit);
  }

  /** Google Static Maps image of the visit's lab. */
  mapPreviewUrl(visit: OrbiAppointment): string {
    return orbiMapPreviewUrl(visit, this.environmentService.googleMapsKey);
  }

  /** True when a map preview can be built for this visit. */
  hasMapPreview(visit: OrbiAppointment): boolean {
    return orbiHasMapPreview(visit, this.environmentService.googleMapsKey);
  }

  onRateVisit(visit: OrbiAppointment): void {
    this.rateVisit.emit(visit);
  }

  onSeeAll(): void {
    this.seeAll.emit();
  }

  /**
   * trackBy for the visit list. Mobile keys on the confirmation number falling
   * back to the instant (`_keyFor`); the index backs rows that carried neither.
   */
  trackByVisit(index: number, visit: OrbiAppointment): string {
    return visit.confirmationNumber ?? visit.dateTime ?? `index-${index}`;
  }
}
